import React, { useState, useCallback, useEffect } from 'react';
import { useVimUserMetadata, useApi, useResourcesWithPII } from '@getvim/internal-vim-os-sdk/react';
import { Provider } from '@getvim/internal-vim-os-sdk/types';
import { getFreeTextQuery } from '@getvim/vim-connect/src/infra/common/freeText/freeTextHandler';
import {
  CurrentContext,
  FreeTextQueryPayload,
  FreeTextResult,
  FreeTextType,
  PriorAuthQueryPayload,
  SetQueryPayload,
  Tab,
  PriorAuthResult as PriorAuthResultInterface,
  PriorAuthData,
  AppPatient,
} from '../types';
import PriorAuthInquiry from './prior-auth-inquiry';
import { useAnalytics } from '../analytics/AnalyticsProvider';
import { getContentSupplierId } from '../analytics/analyticsUtils';
import { PriorAuthQuery as PriorAuthAnalyticsQuery } from '../analytics/types';
import { createLogger } from '../utils/createLogger';
import { checkPriorAuthQuery } from '../logic/checkPriorAuthQuery';
import Content from './content/Content';
import { updateEligibilityContext } from '../logic/updateEligibilityContext';
import {
  getOrderCptIcd,
  OrderCreatedEventPayload,
  getOrderCreatedCptIcd,
} from '../utils/getOrderCptIcd';
import { useNotifications } from '../hooks/useNotifications';
import { searchSessionIdManager } from '../utils/searchSessionIdManager';
import { filterLogContext } from '../utils/filterLogContext';
interface TabsContainerProps {
  currentTab: Tab;
  patient: AppPatient;
  goToTab: (tab: Tab) => void;
}

const logger = createLogger('TabsContainer');

const TabsContainer: React.FC<TabsContainerProps> = ({ patient, currentTab, goToTab }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [cptQueryError, setCptQueryError] = useState(false);
  const [cptQueryLoading, setCptQueryLoading] = useState(false);
  const [searchPriorAuthPayload, setSearchPriorAuthPayload] = useState<PriorAuthQueryPayload>();
  const [searchPriorAuthResult, setSearchPriorAuthResult] = useState<PriorAuthResultInterface>();
  const [selectedProvider, setSelectedProvider] = useState<Provider | undefined>();
  const [priorAuthAnalyticsQuery, setPriorAuthAnalyticsQuery] = useState<PriorAuthAnalyticsQuery>();
  const [cptFreeTextOptions, setCptFreeTextOptions] = useState<FreeTextResult[] | undefined>();
  const [icdFreeTextOptions, setIcdFreeTextOptions] = useState<FreeTextResult[] | undefined>();
  const [icdQueryError, setIcdQueryError] = useState(false);
  const [icdQueryLoading, setIcdQueryLoading] = useState(false);
  const [currentContext, setCurrentContext] = useState<CurrentContext>({});
  const [isSearchInProgress, setIsSearchInProgress] = useState(false);
  const [isEligibilityFailed, setIsEligibilityFailed] = useState<boolean>(false);
  const [launchedComponents, setLaunchedComponents] = useState<{ [key: string]: boolean }>({});
  const [currentOrderCreatedPayload, setCurrentOrderCreatedPayload] = useState<any>();

  const { setDynamicAppSize } = useApi();
  const { ehrState, ehrEvent } = useResourcesWithPII();
  const { orders } = ehrState;
  const { analyticsClient } = useAnalytics();
  const updateLaunchedComponents = (tab: { [key: string]: boolean }) => {
    setLaunchedComponents((components) => ({ ...components, ...tab }));
  };
  const { organization, user } = useVimUserMetadata();

  useEffect(() => {
    if (ehrState.encounter) {
      updateCurrentContext({
        encounterId: ehrState.encounter.identifiers?.ehrEncounterId,
      });
    } else if (currentContext?.encounterId) {
      updateCurrentContext({
        encounterId: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ehrState.encounter]);

  const changeTabWidth = useCallback(
    (tabWidth: string) => {
      setDynamicAppSize(tabWidth as any);
    },
    [setDynamicAppSize],
  );

  const updateCurrentContext = useCallback((updateState: Partial<CurrentContext>) => {
    setCurrentContext((context) => ({ ...context, ...updateState }));
  }, []);
  useEffect(() => {
    updateCurrentContext({
      patient,
    });
    return () => {
      resetAppState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  const clearPriorAuthResult = useCallback(() => {
    setSearchPriorAuthResult(undefined);
    updateCurrentContext({
      selectedProvider: undefined,
    });
    setIsSearchInProgress(false);
  }, [updateCurrentContext]);

  const resetFreeTextOptions = useCallback((type: FreeTextType | FreeTextType[]) => {
    const types: FreeTextType[] = Array.isArray(type) ? type : [type];
    types.forEach((freeTextType) => {
      if (freeTextType === FreeTextType.Cpt) {
        setCptFreeTextOptions(undefined);
        setCptQueryError(false);
        setCptQueryLoading(false);
      }
      if (freeTextType === FreeTextType.Icd) {
        setIcdFreeTextOptions(undefined);
        setIcdQueryError(false);
        setIcdQueryLoading(false);
      }
    });
  }, []);

  const onFreeTextQuery = useCallback(async (payload: FreeTextQueryPayload) => {
    const freeTextPreSendHandlers = {
      [FreeTextType.Icd]: () => {
        setIcdQueryLoading(true);
        setIcdFreeTextOptions(undefined);
      },
      [FreeTextType.Cpt]: () => {
        setCptQueryLoading(true);
        setCptFreeTextOptions(undefined);
      },
    };

    const { type, freeText } = payload;
    if (!freeText) {
      resetFreeTextOptions(type);
      return;
    }
    freeTextPreSendHandlers[type]();
    const result = await getFreeTextQuery(payload);

    if (type === FreeTextType.Cpt) {
      setCptFreeTextOptions(result);
      setCptQueryError(result === undefined);
      setCptQueryLoading(false);
    }
    if (type === FreeTextType.Icd) {
      setIcdFreeTextOptions(result);
      setIcdQueryError(result === undefined);
      setIcdQueryLoading(false);
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const submitPriorAuth = (skipFormValidation = false) => {
    if (!skipFormValidation) {
      if (!searchPriorAuthPayload) {
        return;
      }
      updateCurrentContext({
        searchedCpts: searchPriorAuthPayload.procedures?.length
          ? searchPriorAuthPayload.procedures?.map((p) => p.value)
          : undefined,
        searchedIcds: searchPriorAuthPayload.diagnosis
          ? [searchPriorAuthPayload.diagnosis.value]
          : undefined,
      });
    }

    updateLaunchedComponents({ [Tab.SubmitCase]: false });
    goToTab(Tab.SubmitCase);

    const logContext = filterLogContext(currentContext);
    logger.info('Submit prior auth clicked - redirect to PACS', {
      searchPriorAuthPayload,
      ...logContext,
      noPHI: true,
    });
  };
  const { showNotification, hideNotification } = useNotifications(submitPriorAuth, goToTab);
  const resetAppState = useCallback(
    (shouldUpdateCurrentContext: boolean = true) => {
      goToTab(Tab.PriorAuthInquiry);
      shouldUpdateCurrentContext &&
        updateCurrentContext({
          patient: undefined,
          encounterId: undefined,
          selectedProvider: undefined,
          searchedCpts: undefined,
          searchedIcds: undefined,
        });
      setIsLoading(false);
      resetFreeTextOptions([FreeTextType.Cpt, FreeTextType.Icd]);
      clearPriorAuthResult();
      setPriorAuthAnalyticsQuery(undefined);
      setLaunchedComponents({ [Tab.SubmitCase]: false });
      setSelectedProvider(undefined);
      setSearchPriorAuthPayload(undefined);
      hideNotification();
    },
    [goToTab, updateCurrentContext, resetFreeTextOptions, clearPriorAuthResult, hideNotification],
  );

  const confirmSelectedProvider = useCallback(
    (provider: Provider) => {
      const context = { ...currentContext, selectedProvider: provider };
      updateCurrentContext(context);
      return context;
    },
    [currentContext, updateCurrentContext],
  );

  const updateEligibility = useCallback(
    async (context: CurrentContext): Promise<CurrentContext | undefined> => {
      let timeoutPromise: Promise<CurrentContext> | undefined;

      const updateEligibilityPayload = {
        patient: context.patient,
        encounterId: context.encounterId ?? context.patient?.identifiers?.ehrPatientId,
        npi: context?.selectedProvider?.npi,
        provider: {
          npi: context?.selectedProvider?.npi,
          firstName: context?.selectedProvider?.demographics?.firstName,
          lastName: context?.selectedProvider?.demographics?.lastName,
        },
      };

      const { patient, encounterId, npi, provider } = updateEligibilityPayload;
      const logContext = filterLogContext(updateEligibilityPayload);

      logger.info('Update eligibility executed', {
        ...logContext,
        noPHI: true,
      });

      let isMemberTokenValid = false;

      try {
        if (!patient || !provider) {
          throw new Error('Patient, provider or encounterId is missing');
        }
        const transactionId = (await updateEligibilityContext({
          patient: patient,
          provider: provider as any,
          encounterId: encounterId as string,
        })) as string | undefined;

        isMemberTokenValid = !!transactionId;
        updateCurrentContext({ transactionId });
        timeoutPromise = new Promise<CurrentContext>((resolve) => {
          setTimeout(() => {
            logger.info('Waited 5 seconds for eligibility update', { noPHI: true });
            resolve({ ...context, transactionId });
          }, 5000);
        });
      } catch (error: any) {
        logger.error('Update eligibility failed', {
          ...logContext,
          error,
        });
        logger.error('Update eligibility failed', {
          ...logContext,
          error: error?.message,
          noPHI: true,
        });
      }

      if (!isMemberTokenValid) {
        logger.warning('Update eligibility failed member token not valid', {
          ...logContext,
          noPHI: true,
        });
      }
      setIsEligibilityFailed(!isMemberTokenValid);

      return await timeoutPromise;
    },
    [updateCurrentContext],
  );

  const searchPriorAuth = async (
    payload: PriorAuthQueryPayload,
    type: 'manual' | 'automatic' = 'manual',
  ): Promise<void> => {
    logger.info('Start prior auth search', {
      payload,
      type,
      noPHI: true,
      ...filterLogContext(currentContext),
    });
    const analyticsQuery: PriorAuthAnalyticsQuery = {
      diagnosis_code: payload?.diagnosis?.value,
      procedure_codes: payload?.procedures?.map((procedure) => procedure?.value),
      type: type,
      vim_patient_id: patient?.identifiers?.vimPatientId,
      content_supplier_patient_id: getContentSupplierId(currentContext),
    };
    const eventPayload: PriorAuthQueryPayload = {
      ...payload,
      insurer: patient?.insurance?.insurer,
    };
    const resp = await checkPriorAuthQuery(eventPayload, patient);
    setIsLoading(false);
    setSearchPriorAuthResult(resp as PriorAuthResultInterface);
    if (resp.error) {
      analyticsClient.track('prior_auth_error', {
        ...analyticsQuery,
        error: resp.error,
      });
    }

    searchSessionIdManager.handleShouldUpdate({
      priorAuthQueryPayload: payload.procedures,
      patientId: currentContext.patient?.identifiers?.vimPatientId as string,
    });

    analyticsClient.track('prior_auth_query', {
      ...analyticsQuery,
      success: !resp.error,
      vim_insurer: patient?.insurance?.insurer,
    } as PriorAuthAnalyticsQuery);

    if (type === 'automatic') {
      showNotification(resp as any);
    }
  };
  useEffect(() => {
    const { type, payload } = (ehrEvent as OrderCreatedEventPayload) || {};

    if (
      !orders &&
      (type !== 'orderCreated' || currentOrderCreatedPayload?.order?.id === payload?.order?.id)
    ) {
      resetAppState(false);
      return;
    }
    setCurrentOrderCreatedPayload(ehrEvent?.payload);
    let result;
    if (orders) {
      result = getOrderCptIcd(orders as any);
    } else if (ehrEvent) {
      result = getOrderCreatedCptIcd(ehrEvent as OrderCreatedEventPayload);
    }

    if (result.procedures?.length > 0) {
      setSearchPriorAuthPayload(result);
      void searchPriorAuth(result, 'automatic');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, ehrEvent]);

  const onSearchClick = useCallback(
    (payload: PriorAuthQueryPayload) => searchPriorAuth(payload, 'manual'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patient],
  );

  useEffect(
    () => analyticsClient.setContext(currentContext, currentTab),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentContext, currentTab],
  );

  return (
    <Content
      currentTab={currentTab}
      changeTabWidth={changeTabWidth}
      onFreeTextQuery={onFreeTextQuery}
      onSearchClick={onSearchClick}
      cptFreeTextOptions={cptFreeTextOptions}
      priorAuthResult={searchPriorAuthResult}
      queryPayload={searchPriorAuthPayload}
      setQueryPayload={setSearchPriorAuthPayload as SetQueryPayload}
      cptQueryLoading={cptQueryLoading}
      cptQueryError={cptQueryError}
      icdFreeTextOptions={icdFreeTextOptions}
      icdQueryLoading={icdQueryLoading}
      icdQueryError={icdQueryError}
      resetFreeTextOptions={resetFreeTextOptions}
      isSearchInProgress={isLoading}
      setIsSearchInProgress={setIsLoading}
      submitPriorAuth={submitPriorAuth}
      useTabs={true}
      clearPriorAuthResult={clearPriorAuthResult}
      displaySelectProviderScreen={true}
      goToTab={goToTab}
      launchedComponents={launchedComponents}
      updateLaunchedComponents={updateLaunchedComponents}
      currentContext={currentContext}
      setSelectedProvider={setSelectedProvider}
      selectedProvider={selectedProvider}
      confirmSelectedProvider={confirmSelectedProvider}
      isEligibilityFailed={isEligibilityFailed}
      updateEligibility={updateEligibility}
    />
  );
};

export default TabsContainer;
